import { gsap } from "gsap";

const SCROLL_EDGE_TOLERANCE = 1;
const MOBILE_MEDIA_QUERY = "(max-width: 767px), (max-height: 767px) and (pointer: coarse)";

export function infoPanelFor(item) {
  const imagePanel = item?.nextElementSibling;
  const infoPanel = imagePanel?.nextElementSibling;
  return infoPanel?.classList.contains("project-viewer-v2__info-panel") ? infoPanel : null;
}

// On mobile the description and meta scroll together as one unit (the rail stays
// outside the scroll container so its sticky arrows don't move); on desktop only
// the description column scrolls, leaving the meta column in place beside it.
function getScrollElement(panel) {
  const selector = window.matchMedia(MOBILE_MEDIA_QUERY).matches ? ".project-viewer-v2__info-scroll > .min-w-0" : "[data-info-scroll-content]";
  return panel?.querySelector(selector);
}

export function syncInfoScrollIndicators(panel) {
  const scrollContent = getScrollElement(panel);
  const upIndicator = panel?.querySelector("[data-info-scroll-up]");
  const downIndicator = panel?.querySelector("[data-info-scroll-down]");
  if (!scrollContent || !upIndicator || !downIndicator) return;

  const maxScrollTop = Math.max(0, scrollContent.scrollHeight - scrollContent.clientHeight);
  upIndicator.classList.toggle("invisible", scrollContent.scrollTop <= SCROLL_EDGE_TOLERANCE);
  downIndicator.classList.toggle("invisible", maxScrollTop <= SCROLL_EDGE_TOLERANCE || scrollContent.scrollTop >= maxScrollTop - SCROLL_EDGE_TOLERANCE);
}

export function setupInfoScrollIndicators(root) {
  const panels = Array.from(root.querySelectorAll(".project-viewer-v2__info-panel"));
  const mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
  const onScroll = (event) => syncInfoScrollIndicators(event.currentTarget.closest(".project-viewer-v2__info-panel"));
  const syncAll = () => panels.forEach(syncInfoScrollIndicators);

  let scrollContents = [];

  // Rebinds to whichever element is actually scrollable at the current breakpoint.
  const bindScrollListeners = () => {
    scrollContents.forEach((scrollContent) => scrollContent?.removeEventListener("scroll", onScroll));
    scrollContents = panels.map((panel) => getScrollElement(panel));
    scrollContents.forEach((scrollContent) => scrollContent?.addEventListener("scroll", onScroll, { passive: true }));
  };

  const onBreakpointChange = () => {
    bindScrollListeners();
    syncAll();
  };

  bindScrollListeners();
  window.addEventListener("resize", syncAll);
  mobileQuery.addEventListener("change", onBreakpointChange);
  requestAnimationFrame(syncAll);

  return () => {
    scrollContents.forEach((scrollContent) => scrollContent?.removeEventListener("scroll", onScroll));
    window.removeEventListener("resize", syncAll);
    mobileQuery.removeEventListener("change", onBreakpointChange);
  };
}

export function setInfoPanelOpen(panel, isOpen) {
  gsap.killTweensOf(panel);

  if (isOpen) {
    panel.classList.remove("hidden");
    panel.setAttribute("aria-hidden", "false");
    gsap.set(panel, { height: 0 });
    const targetHeight = panel.scrollHeight;
    gsap.to(panel, {
      height: targetHeight,
      duration: 0.4,
      ease: "power2.inOut",
      onComplete: () => {
        gsap.set(panel, { height: "auto" });
        syncInfoScrollIndicators(panel);
      },
    });
    return;
  }

  gsap.set(panel, { height: panel.offsetHeight });
  gsap.to(panel, {
    height: 0,
    duration: 0.4,
    ease: "power2.inOut",
    onComplete: () => {
      panel.classList.add("hidden");
      panel.setAttribute("aria-hidden", "true");
      gsap.set(panel, { clearProps: "height" });
    },
  });
}

export function setInfoPanelOpenForItem(item, isOpen) {
  const panel = infoPanelFor(item);
  const btn = item?.nextElementSibling?.querySelector('[g-ref="info"]');
  if (!panel) return;

  setInfoPanelOpen(panel, isOpen);

  if (btn) {
    btn.dataset.open = isOpen ? "true" : "false";
    btn.setAttribute("aria-label", isOpen ? "Hide project info" : "Show project info");
  }
}

export function resetInfoPanel(item) {
  const panel = infoPanelFor(item);
  const btn = item?.nextElementSibling?.querySelector('[g-ref="info"]');

  if (panel) {
    gsap.killTweensOf(panel);
    panel.classList.add("hidden");
    panel.setAttribute("aria-hidden", "true");
    gsap.set(panel, { clearProps: "height" });
  }

  if (btn) {
    btn.dataset.open = "false";
    btn.setAttribute("aria-label", "Show project info");
  }
}

export function setActiveItem(item, activeItem) {
  if (activeItem) activeItem.dataset.active = "false";

  if (item) item.dataset.active = "true";
  return item;
}
